import { API_ENDPOINTS, ERROR_MESSAGES, STORAGE_KEYS } from '../utils/constants';
import databaseService from './database';

/**
 * Base API service for SafeLine Admin Dashboard
 * Integrated with Firestore database
 */

// Default request headers
const getDefaultHeaders = () => {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Handle API response
const handleResponse = async (response) => {
  if (!response.ok) {
    // Handle different error status codes
    if (response.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      window.location.href = '/login';
      throw new Error(ERROR_MESSAGES.SESSION_EXPIRED);
    }
    
    if (response.status === 403) {
      throw new Error(ERROR_MESSAGES.PERMISSION_DENIED);
    }
    
    if (response.status === 404) {
      throw new Error('Resource not found');
    }
    
    if (response.status === 500) {
      throw new Error(ERROR_MESSAGES.SERVER_ERROR);
    }
    
    // Try to get error message from response
    try {
      const errorData = await response.json();
      throw new Error(errorData.message || ERROR_MESSAGES.GENERIC);
    } catch (e) {
      throw new Error(ERROR_MESSAGES.GENERIC);
    }
  }
  
  // For successful responses, parse JSON
  try {
    return await response.json();
  } catch (e) {
    // If response cannot be parsed as JSON, return empty object
    return {};
  }
};

/**
 * Make a GET request to the API
 * @param {string} endpoint - API endpoint
 * @param {object} params - Query parameters
 * @returns {Promise} Promise resolving to response data
 */
export const get = async (endpoint, params = {}) => {
  try {
    // Build URL with query parameters
    const url = new URL(endpoint, window.location.origin);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });
    
    // Make request
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getDefaultHeaders(),
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API GET Error:', error);
    throw error;
  }
};

/**
 * Make a POST request to the API
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body data
 * @returns {Promise} Promise resolving to response data
 */
export const post = async (endpoint, data = {}) => {
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API POST Error:', error);
    throw error;
  }
};

/**
 * Make a PUT request to the API
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body data
 * @returns {Promise} Promise resolving to response data
 */
export const put = async (endpoint, data = {}) => {
  try {
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API PUT Error:', error);
    throw error;
  }
};

/**
 * Make a DELETE request to the API
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body data (optional)
 * @returns {Promise} Promise resolving to response data
 */
export const del = async (endpoint, data = null) => {
  try {
    const options = {
      method: 'DELETE',
      headers: getDefaultHeaders(),
    };
    
    if (data) {
      options.body = JSON.stringify(data);
    }
    
    const response = await fetch(endpoint, options);
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API DELETE Error:', error);
    throw error;
  }
};

/**
 * Upload a file to the API
 * @param {string} endpoint - API endpoint
 * @param {File} file - File to upload
 * @param {object} additionalData - Additional form data
 * @returns {Promise} Promise resolving to response data
 */
export const uploadFile = async (endpoint, file, additionalData = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add any additional data to form data
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });
    
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const headers = {
      'Authorization': token ? `Bearer ${token}` : '',
      // Note: Do not set Content-Type header for multipart/form-data
    };
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('API File Upload Error:', error);
    throw error;
  }
};

// API service object with database integration
const apiService = {
  // Auth endpoints (still use HTTP for authentication)
  auth: {
    login: (credentials) => post(API_ENDPOINTS.LOGIN, credentials),
    loginWithPin: (credentials) => post(API_ENDPOINTS.LOGIN_WITH_PIN, credentials),
    logout: () => post(API_ENDPOINTS.LOGOUT),
  },
  
  // Users endpoints
  users: {
    getAll: (params) => databaseService.users.getAll(params?.school_id),
    getById: (id) => databaseService.users.getById(id),
    getByFirebaseUid: (uid) => databaseService.users.getByFirebaseUid(uid),
    getByEmail: (email) => databaseService.users.getByEmail(email),
    updateFirebaseUid: (userId, firebaseUid) => databaseService.users.updateFirebaseUid(userId, firebaseUid),
    create: (userData) => databaseService.users.create(userData),
    update: (id, userData) => databaseService.users.update(id, userData),
    delete: (id) => databaseService.users.delete(id),
  },
  
  // Students endpoints
  students: {
    getAll: (params) => databaseService.students.getAll(params?.school_id),
    getById: (id) => databaseService.students.getById(id),
    getByClass: (classId) => databaseService.students.getByClass(classId),
    create: (studentData) => databaseService.students.create(studentData),
    update: (id, studentData) => databaseService.students.update(id, studentData),
    updateWalkingStatus: (id, isWalking, scheduleId) => databaseService.students.updateWalkingStatus(id, isWalking, scheduleId),
    delete: (id) => databaseService.students.delete(id),
  },
  
  // Guardians endpoints
  guardians: {
    getAll: (params) => databaseService.guardians.getAll(params?.school_id),
    getById: (id) => databaseService.guardians.getById(id),
    getPendingApproval: (schoolId) => databaseService.guardians.getPendingApproval(schoolId),
    create: (guardianData) => databaseService.guardians.create(guardianData),
    update: (id, guardianData) => databaseService.guardians.update(id, guardianData),
    approve: (id) => databaseService.guardians.approve(id),
    deny: (id) => databaseService.guardians.deny(id),
    delete: (id) => databaseService.guardians.delete(id),
  },
  
  // Guests endpoints
  guests: {
    getAll: (params) => databaseService.guests.getAll(params?.school_id),
    getById: (id) => databaseService.guests.getById(id),
    getPendingApproval: (schoolId) => databaseService.guests.getPendingApproval(schoolId),
    create: (guestData) => databaseService.guests.create(guestData),
    update: (id, guestData) => databaseService.guests.update(id, guestData),
    approve: (id) => databaseService.guests.approve(id),
    deny: (id) => databaseService.guests.deny(id),
    delete: (id) => databaseService.guests.delete(id),
  },
  
  // Pickup logs endpoints
  pickupLogs: {
    getAll: (params) => databaseService.pickupLogs.getAll(params?.school_id, params?.limit),
    getByStudent: (studentId) => databaseService.pickupLogs.getByStudent(studentId),
    getByDateRange: (startDate, endDate, schoolId) => databaseService.pickupLogs.getByDateRange(startDate, endDate, schoolId),
    search: (searchParams) => databaseService.pickupLogs.search(searchParams),
    create: (logData) => databaseService.pickupLogs.create(logData),
  },
  
  // Walking Schedules endpoints
  walkingSchedules: {
    getAll: (params) => databaseService.walkingSchedules.getAll(params?.school_id),
    getByStudent: (studentId) => databaseService.walkingSchedules.getByStudent(studentId),
    getByGuardian: (guardianId) => databaseService.walkingSchedules.getByGuardian(guardianId),
    create: (scheduleData) => databaseService.walkingSchedules.create(scheduleData),
    update: (id, scheduleData) => databaseService.walkingSchedules.update(id, scheduleData),
    approve: (id) => databaseService.walkingSchedules.approve(id),
    deny: (id) => databaseService.walkingSchedules.deny(id),
    delete: (id) => databaseService.walkingSchedules.delete(id),
  },
  
  // Guardian Tags endpoints
  guardianTags: {
    getAll: (params) => databaseService.guardianTags.getAll(params?.school_id),
    getByGuardian: (guardianId) => databaseService.guardianTags.getByGuardian(guardianId),
    create: (tagData) => databaseService.guardianTags.create(tagData),
    update: (id, tagData) => databaseService.guardianTags.update(id, tagData),
    deactivate: (id) => databaseService.guardianTags.deactivate(id),
    delete: (id) => databaseService.guardianTags.delete(id),
  },
  
  // Stats endpoints
  stats: {
    getDailyStats: (date, schoolId) => databaseService.stats.getDailyStats(date, schoolId),
    getWeeklyStats: (weekStart, schoolId) => databaseService.stats.getWeeklyStats(weekStart, schoolId),
    getMonthlyStats: (month, schoolId) => databaseService.stats.getMonthlyStats(month, schoolId),
  },
  
  // Schools endpoints
  schools: {
    getAll: () => databaseService.schools.getAll(),
    getById: (id) => databaseService.schools.getById(id),
    create: (schoolData) => databaseService.schools.create(schoolData),
    update: (id, schoolData) => databaseService.schools.update(id, schoolData),
    delete: (id) => databaseService.schools.delete(id),
  },
  
  // Real-time subscriptions
  realtime: {
    listenToPickupLogs: (schoolId, callback) => databaseService.realtime.listenToPickupLogs(schoolId, callback),
    listenToGuestNotifications: (schoolId, callback) => databaseService.realtime.listenToGuestNotifications(schoolId, callback),
    listenToStudentUpdates: (schoolId, callback) => databaseService.realtime.listenToStudentUpdates(schoolId, callback),
  },

  // Settings endpoints
  settings: {
    getAll: (userId) => databaseService.settings.getAll(userId),
    getByCategory: (userId, category) => databaseService.settings.getByCategory(userId, category),
    save: (userId, category, settingsData) => databaseService.settings.save(userId, category, settingsData),
    reset: (userId, category) => databaseService.settings.reset(userId, category),
  },
};

export default apiService;
